import { type Router, type RouterHistory, createMemoryHistory, createRouter as _createRouter, createWebHistory, type RouteLocationRaw } from 'vue-router'
import { getAppRequestString } from '@/js/app-bridge/helpers'
import { getRoutes } from '@/js/router/routes'
import { sessionDurationMin } from '@/js/services/Utils'
import skiplinks from '@/js/components/rogue-a11y.vue'
import { loadRoute } from '@/js/router/loadRoutes'
import type { IRogue } from '@/js/services/RogueTypes'

declare module 'vue-router' {
  interface RouteMeta {
    defaultYear?: number,
    fullWidth?: boolean,
    id?: string | number,
    isCMSPage?: boolean,
    isFunneled?: boolean,
    removeAboveHeader?: boolean,
    removeCookieAgree?: boolean,
    removeBanners?: boolean,
    siblingScroll?: boolean,
    type?: string,
    wide?: boolean,
    year?: number,
  }
}

function setActiveCategory(categoryId: number) {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !document) {
    return
  }
  const active = document.querySelectorAll('a[categoryid].active')

  for (let i = 0; i < active.length; i += 1) {
    active[i].classList.remove('active')
  }

  if (categoryId) {
    const toActivate = document.querySelectorAll(`a[categoryid="${categoryId}"]:not(.crumb-link)`)
    for (let i = 0; i < toActivate.length; i += 1) {
      toActivate[i].classList.add('active')
    }
  }
}

function scrollAppToTop() {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !document) {
    return
  }

  const options: ScrollToOptions = { top: 0, left: 0, behavior: 'instant' }

  document.body?.scrollTo(options)
  document.documentElement?.scrollTo(options)
  window.scrollTo(options)
}

const RESTORE_SCROLL_POLL_MS = 50
const RESTORE_SCROLL_TIMEOUT_MS = 3000

function isTallEnoughFor(top: number): boolean {
  return document.documentElement.scrollHeight >= top + window.innerHeight
}

/**
 * Resolves once the page is tall enough to scroll to `top`, or after a timeout.
 * Listing pages (deals, categories) render placeholder cards while their products load, so on back navigation the page
 * can be too short for the saved position and the browser would clamp it to the bottom (the footer).
 */
function waitForScrollHeight(top: number): Promise<void> {
  return new Promise((resolve) => {
    const start = Date.now()
    const check = () => {
      if (isTallEnoughFor(top) || Date.now() - start >= RESTORE_SCROLL_TIMEOUT_MS) {
        resolve()
      } else {
        setTimeout(check, RESTORE_SCROLL_POLL_MS)
      }
    }
    check()
  })
}

export function createRouter(rogue: IRogue, isServer: boolean): Router {
  const base: string = rogue.l10n.path || '/'
  const history: RouterHistory = isServer ? createMemoryHistory(base) : createWebHistory(base)

  const router = _createRouter({
    routes               : getRoutes(rogue),
    history,
    linkExactActiveClass : 'active',
    scrollBehavior(to, from, savedPosition) {
      const prevPageType = to.meta?.type
      const currentPageType = from.meta?.type

      if (savedPosition) {
        // Restore straight away when we can - on refresh the server-rendered page is already full height, but deals
        // swaps its products for placeholders once it mounts, so waiting even a tick would scroll the collapsed page
        if (typeof document === 'undefined' || !savedPosition.top || isTallEnoughFor(savedPosition.top)) {
          return savedPosition
        }

        return waitForScrollHeight(savedPosition.top).then(() => savedPosition)
      }

      if (to.hash) {
        return new Promise((resolve) => {
          setTimeout(() => {
            if (to.hash) {
              resolve({ el: to.hash })
            }
          }, 300)
        })
      }

      if (!rogue.appContext && prevPageType === 'product' && currentPageType === 'product' && to.path === from.path) {
        return undefined
      }

      if (!rogue.appContext && prevPageType === 'cms' && currentPageType === 'cms') {
        const fromPath = from.path.split('/')
        const toPath = to.path.split('/')

        // If we are going between CMS tabs of sub pages, dont scroll (Invitational)
        if (fromPath[1] === toPath[1]) {
          return undefined
        }
      }

      // Dont scroll between siblings (e.g. for tabs on Gym builder)
      if (to.matched.length === from.matched.length
        && to.matched[0] === from.matched[0]
        && to.matched[0].meta.siblingScroll === false) {
        return undefined
      }

      if (
        to.path !== from.path
        // Categories pagination
        || to.query?.page_number !== from.query?.page_number
      ) {
        if (rogue.appContext) {
          scrollAppToTop()

          return undefined
        }

        return { left: 0, top: 0 }
      }

      return undefined
    },
  })

  let routeCount = 0
  let routeChangeStart = Date.now()
  let lastLoadRoutePath = ''
  let loadRouteRetries = 0

  // Full-reload target that keeps the native app's context params on the new document
  const buildReloadHref = (fullPath: string) => {
    const href = history.createHref(fullPath)

    return `${href}${getAppRequestString(rogue.appContext, href)}`
  }

  router.beforeEach(async (to, from) => {

    if (to.path.includes('//')) {
      to.path = to.path.replace(/\/\//g, '/')
    }

    // Sometimes we want to force a full page reload (but not on SSR)
    if (!rogue.ssr) {
      const allowedQuoteSubdomains = ['local', 'm2', 'm2stagingadmin', 'm2sprintadmin']
      const parts = window.location.host.split('.')
      const subdomain = parts.length > 2 ? parts[0] : ''

      // Only allow people on our internal network to go to the OM quote page (isAdminRoute)
      if (to.meta.isAdminRoute && !allowedQuoteSubdomains.includes(subdomain)) {
        // send them to the 404 page
        window.location.href = "/404"
      }
      
      // Check if the session is super old, in which case lets just force a full route change
      if (sessionDurationMin() > 60 * 24) {
        rogue.log.info('Session duration is longer than 1 day - doing full reload')

        window.location.href = buildReloadHref(to.fullPath)
      }
      // If there is a new release, forcing a full route change will cause it to reload the latest route
      if (rogue.hasNewRelease && routeCount > 0) {
        rogue.log.info(`New release found - doing full page reload on transition from ${from?.fullPath} to ${to.fullPath}`)
        window.location.href = buildReloadHref(to.fullPath)
      }

      // Forced redirects @todo not sure we use these
      if (to.meta?.ssrRedirect?.indexOf('http') === 0) {
        window.location = to.meta.ssrRedirect
      }
    }

    // Set our local variable, for tracking purposes
    routeChangeStart = Date.now()

    if (!to.matched?.length) {
      // Did not find a route...
      rogue.log.debug(`No route loaded found for ${to.path} (prefix: ${rogue.l10n.path})`)

      let sanitizedRoute = to.path.trim()
        .toLowerCase()
        .split(' ')[0]
        .split('%20')[0]
        .split('?')[0]
        .split('#')[0]
        .replace(/[()]/g, '')

      if (rogue.l10n.path && sanitizedRoute.startsWith(rogue.l10n.path)) {
        sanitizedRoute = sanitizedRoute
          .replace(new RegExp(`^${rogue.l10n.path}/`, 'i'), '/')
          .replace(new RegExp(`^${rogue.l10n.path}$`, 'i'), '')
      }

      // Guard against infinite redirect loops: if we keep trying the
      // same path and the added route still doesn't match, bail out.
      if (sanitizedRoute === lastLoadRoutePath) {
        loadRouteRetries++
        if (loadRouteRetries > 2) {
          rogue.log.warn(`Route loop detected for ${sanitizedRoute}, stopping redirect`)
          lastLoadRoutePath = ''
          loadRouteRetries = 0
          
          return false
        }
      } else {
        lastLoadRoutePath = sanitizedRoute
        loadRouteRetries = 0
      }

      await loadRoute(sanitizedRoute, rogue)

      // Reload the route to match to the newly added route
      return { path: sanitizedRoute, query: to.query, hash: to.hash }
    }
  })

  // We dont want the old xlink-href alternate tags sticking around after they are expired.
  // These are only generated on SSR loads, so they could point to the wrong resource after a SPA page change.
  function removeOldHrefLangLinks() {
    try {
      const altLinks = document.querySelectorAll('link[rel=alternate]')
      for (let i = 0; i < altLinks.length; i += 1) {
        altLinks[i].remove()
      }
    } catch (e) {
      //
    }
  }

  router.afterEach((to, from) => {
    // rogue.log.debug(`Route change complete ${to.path}`)
    routeCount += 1

    // This is called before the component is created, so we can use this to get the feature flag system ready
    // for the new route.
    try {
      rogue.featureFlags.updateAttributes()
    } catch (e) {
      const error = e as Error
      rogue.log.warn('Error setting attributes after route', error)
    }

    // We dont want to track stuff or manipulate the dom in SSR
    if (rogue.ssr) {
      return
    }

    // This updates all the link tags on the page that have categoryid="" so that they are red
    setActiveCategory(to.meta?.type === 'category' ? to.meta.id : undefined)

    // Once we have visited a new page, remove the xlink:href tags, which were only valid for the landing page
    if (routeCount === 2) {
      removeOldHrefLangLinks()
    }

    if (to.name !== 'nativeAppPreloader') {
      rogue.track.track({
        type  : 'pageView',
        route : {
          entityId : to.meta?.id,
          type     : to.meta?.type,
          fullPath : to.fullPath,
          from     : from.path,
          path     : to.meta?.trackPath || to.path,
          count    : routeCount,
          title    : to.name || to.path,
          duration : Date.now() - routeChangeStart,
        },
      })
    }

    // Accesibility: Change current element focus up to the top of the page, for screen readers.
    // This means that after changing page, pressing "Tab" will start you at the top of the page instead of at the bottom
    if (to.path !== from.path) {
      const skipLinks = document.getElementById('skiplink')
      if (skiplinks && skipLinks instanceof HTMLElement) {
        skipLinks.focus()
      }
    }
  })

  // vue router throws some weird Duplicate Route errors. If we dont catch them, they show up in the console / logs and can break SSR
  // Probably our fault, but they dont cause actual issues so we can just swallow them
  const originalPush = router.push.bind(router)
  const originalReplace = router.replace.bind(router)

  router.push = function newPush(loc: RouteLocationRaw) {
    if (typeof loc === 'string' && rogue.l10n.path && loc.startsWith(rogue.l10n.path)) {
      loc = loc
        .replace(new RegExp(`^${rogue.l10n.path}/`, 'i'), '/')
        .replace(new RegExp(`^${rogue.l10n.path}$`, 'i'), '')
    }

    return originalPush(loc)
      .catch(err => {
        if (err.name !== 'NavigationDuplicated' && err.message.indexOf('Redirected') !== 0) {
          if (rogue.ssr) {
            rogue.log.error(`SSR Route Change error from ${rogue.initialRoute} to ${loc}`, err)
          } else {
            rogue.log.error(`Error with route change to ${loc}`, err)
          }
          throw err
        }
      })
  }

  router.replace = function newReplace(loc: RouteLocationRaw) {
    return originalReplace(loc).catch(err => {
      if (err.name !== 'NavigationDuplicated') {
        throw err
      }
    })
  }

  return router
}
